import { useEffect, useRef } from 'react';
import { isRoundBasedFormat } from '@draughts/shared';
import { useTournamentStore } from '../../store/tournament-store';
import { useAuthStore } from '../../store/auth-store';
import TournamentStandings from './TournamentStandings';
import '../shared/classifica-in-partita.css';

/**
 * La classifica del torneo vista DA DENTRO una partita.
 *
 * E' la stessa `TournamentStandings` della pagina del torneo, con i dati che lo
 * store gia' tiene aggiornati durante il gioco: chi gioca resta nella stanza
 * socket del torneo, e `tournament:standings` riscrive `standings` a ogni
 * partita chiusa. Niente chiamate in piu', niente server.
 *
 * Legge lo store da se' invece di ricevere le righe come prop: cosi' chi la
 * monta (la colonna laterale, che e' `memo`, e il pannello a tutto schermo) le
 * passa un elemento che non cambia mai, e il ridisegno che l'orologio provoca
 * in `TournamentGame` piu' volte al secondo non arriva fin qui.
 *
 * Senza l'occhio «guarda la partita» (`onSpectate` assente): mentre si gioca
 * non si guarda la partita di un altro, e il server lo rifiuterebbe comunque.
 */
export default function ClassificaInPartita() {
  const standings = useTournamentStore((s) => s.standings);
  const format = useTournamentStore((s) => s.tournamentFormat);
  const twoLeg = useTournamentStore((s) => s.currentTournament?.twoLeg ?? false);
  /*
   * Lo stesso «finito» della lobby. Chi chiude l'ultima partita del torneo vede
   * qui la classifica FINALE (arriva con `tournament:ended` mentre e' ancora
   * nella schermata della partita), e solo a torneo finito ci sono le medaglie.
   */
  const torneoFinito = useTournamentStore(
    (s) => s.currentTournament?.status === 'finished' || s.status === 'finished',
  );
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const contenitore = useRef<HTMLDivElement>(null);

  /*
   * All'apertura si parte dalla PROPRIA riga, non dalla cima.
   *
   * Con quaranta iscritti la riga di chi gioca puo' stare tre schermate sotto,
   * e la domanda che si fa aprendo la classifica a meta' partita e' «dove
   * sono io?». Lo scorrimento e' fatto a mano sul contenitore e non con
   * `scrollIntoView`, che scorrerebbe anche gli antenati — la colonna della
   * partita compresa — spostando la damiera.
   *
   * Solo al montaggio: se scorresse a ogni aggiornamento, chi sta leggendo la
   * cima verrebbe riportato giu' ogni volta che un'altra partita finisce.
   */
  useEffect(() => {
    const box = contenitore.current;
    const mia = box?.querySelector<HTMLElement>('[data-mia]');
    if (!box || !mia) return;
    box.scrollTop = mia.offsetTop - (box.clientHeight - mia.offsetHeight) / 2;
  }, []);

  return (
    <div className="classifica-in-partita" ref={contenitore} data-testid="classifica-in-partita">
      <TournamentStandings
        standings={standings}
        aTurni={isRoundBasedFormat(format)}
        girone={format === 'round_robin'}
        manche={twoLeg ? 2 : 1}
        torneoFinito={torneoFinito}
        mioUserId={userId}
      />
    </div>
  );
}
